import i18n from "@/i18n"
import { formatDuration } from "@/shared/datetime"

export type BookingPolicyTileKind = "time_limit" | "running_late" | "group_reservations" | "cancellation"

export type BookingPolicyDetailSection = {
  id: string
  title: string
  content?: string | null
}

export type BookingPolicyTileViewModel = {
  kind: BookingPolicyTileKind
  enabled: boolean
  subtitle: string
}

type BookingPolicyOptions = {
  includeDisabled?: boolean
  includeUnconfigured?: boolean
  orderedKinds?: BookingPolicyTileKind[]
}

type BookingPolicyContext = {
  availableBookingPolicyTiles?: BookingPolicyTileKind[] | null
  bookingPolicyTiles?: BookingPolicyTileKind[] | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
  noshowAutoCancelAfter?: number | null
  largeGroupThreshold?: number | null
  cancellationDeadline?: number | null
  phone?: string | null
  email?: string | null
}

const BOOKING_POLICY_TILE_KEYS: BookingPolicyTileKind[] = [
  "time_limit",
  "running_late",
  "group_reservations",
  "cancellation",
]

const uniqueKinds = (kinds?: Array<BookingPolicyTileKind | null> | null) => {
  if (!kinds?.length) return []

  return Array.from(new Set(kinds.filter((kind): kind is BookingPolicyTileKind => Boolean(kind))))
}

const buildTimeLimitSubtitle = (minReservationTime?: number | null, maxReservationTime?: number | null) => {
  if (minReservationTime && maxReservationTime) {
    if (minReservationTime === maxReservationTime) {
      return i18n.t("app.booking_policy.tiles.time_limit.fixed", {
        time: formatDuration(minReservationTime),
      })
    }

    return i18n.t("app.booking_policy.tiles.time_limit.range", {
      min: formatDuration(minReservationTime),
      max: formatDuration(maxReservationTime),
    })
  }

  if (maxReservationTime) {
    return i18n.t("app.booking_policy.tiles.time_limit.maximum", {
      time: formatDuration(maxReservationTime),
    })
  }

  if (minReservationTime) {
    return i18n.t("app.booking_policy.tiles.time_limit.minimum", {
      time: formatDuration(minReservationTime),
    })
  }

  return null
}

const buildGroupReservationsSubtitle = (
  phone?: string | null,
  email?: string | null,
  largeGroupThreshold?: number | null,
) => {
  if (phone && email) {
    return i18n.t("app.booking_policy.tiles.group_reservations.call_email")
  }

  if (phone) {
    return i18n.t("app.booking_policy.tiles.group_reservations.call")
  }

  if (email) {
    return i18n.t("app.booking_policy.tiles.group_reservations.email")
  }

  if (largeGroupThreshold) {
    return i18n.t("app.booking_policy.tiles.group_reservations.threshold", {
      count: largeGroupThreshold,
    })
  }

  return null
}

const buildTileSubtitle = (kind: BookingPolicyTileKind, context: BookingPolicyContext) => {
  switch (kind) {
    case "time_limit":
      return buildTimeLimitSubtitle(context.minReservationTime, context.maxReservationTime)
    case "running_late":
      return context.noshowAutoCancelAfter
        ? i18n.t("app.booking_policy.tiles.running_late.value", {
            time: formatDuration(context.noshowAutoCancelAfter),
          })
        : null
    case "group_reservations":
      return buildGroupReservationsSubtitle(context.phone, context.email, context.largeGroupThreshold)
    case "cancellation":
      return context.cancellationDeadline
        ? i18n.t("app.booking_policy.tiles.cancellation.value", {
            time: formatDuration(context.cancellationDeadline),
          })
        : null
    default:
      return null
  }
}

const buildFallbackSubtitle = (kind: BookingPolicyTileKind) => {
  return i18n.t(`app.booking_policy.tiles.${kind}.placeholder`)
}

export const getBookingPolicyTileTitle = (kind: BookingPolicyTileKind) => {
  return i18n.t(`app.booking_policy.tiles.${kind}.title`)
}

export const buildBookingPolicyTileOrder = (
  availableBookingPolicyTiles?: BookingPolicyTileKind[] | null,
  bookingPolicyTiles?: BookingPolicyTileKind[] | null,
) => {
  const availableKinds = uniqueKinds(availableBookingPolicyTiles)
  const enabledKinds = uniqueKinds(bookingPolicyTiles).filter((kind) => availableKinds.includes(kind))
  const remainingKinds = availableKinds.filter((kind) => !enabledKinds.includes(kind))

  return [...enabledKinds, ...remainingKinds]
}

export const buildBookingPolicyTiles = (context: BookingPolicyContext, options: BookingPolicyOptions = {}) => {
  const { includeDisabled = false, includeUnconfigured = false, orderedKinds } = options
  const enabledKinds = uniqueKinds(context.bookingPolicyTiles)
  const kinds = uniqueKinds(
    orderedKinds?.length
      ? orderedKinds
      : buildBookingPolicyTileOrder(context.availableBookingPolicyTiles, context.bookingPolicyTiles),
  )

  return kinds.flatMap<BookingPolicyTileViewModel>((kind) => {
    const enabled = enabledKinds.includes(kind)
    const subtitle = buildTileSubtitle(kind, context)

    if (!enabled && !includeDisabled) {
      return []
    }

    if (!subtitle && !includeUnconfigured) {
      return []
    }

    return [
      {
        kind,
        enabled,
        subtitle: subtitle || buildFallbackSubtitle(kind),
      },
    ]
  })
}

export const buildBookingPolicySections = (sections?: BookingPolicyDetailSection[] | null) => {
  return sections?.filter((section) => section.content?.trim()) || []
}

export const getAllBookingPolicyTileKinds = () => BOOKING_POLICY_TILE_KEYS

const BOOKING_POLICY_TILE_VALUES: ReadonlySet<string> = new Set(BOOKING_POLICY_TILE_KEYS)

export const parseBookingPolicyTileKinds = (value: unknown): BookingPolicyTileKind[] | null => {
  if (!Array.isArray(value)) return null

  return value.filter(
    (tile): tile is BookingPolicyTileKind => typeof tile === "string" && BOOKING_POLICY_TILE_VALUES.has(tile),
  )
}

type ReservationRuleSection = {
  id: string
  identifier?: string | null
  title?: string | null
  content?: string | null
  position: number
}

export type BookingPolicySectionResult = {
  title?: string | null
  content?: string | null
}

export const buildBookingPolicySectionFromRules = (
  sections?: ReservationRuleSection[] | null,
): BookingPolicySectionResult | null => {
  if (!sections?.length) return null

  const section = [...sections]
    .sort((a, b) => a.position - b.position)
    .find((s) => s.identifier === "booking_policy" && s.content?.trim())

  if (!section) return null

  return { title: section.title, content: section.content }
}
